import { When } from "react-if";
import { useMemo, useState } from "react";
import {
  PolicyType,
  PolicyTargetStatus,
  PolicyState,
  langauge,
} from "shared";

import { useCurrentGroup } from "../../../../hooks/useGroups";
import { classNames } from "../../../../../common/lib/classNames";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { useAccountingSystemTerm } from "../../../../hooks/useAccountingSystemTerm";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { useAllowedFeature } from "../../../../hooks/useAllowedFeature";
import { Notification } from "../../../../../common/Atoms/Notification";

import { PolicyColumn } from "./PolicyColumn";
import { SetUpForMe } from "./SetUpForMe";

const order: PolicyType[] = [
  PolicyType.LATE_FEE,
  PolicyType.REMINDER,
  PolicyType.STATEMENT,
  PolicyType.SMS,
  PolicyType.ESCALATION,
  PolicyType.PAYMENT_PLAN_REMINDER,
  PolicyType.QUOTE_EMAIL,
  PolicyType.DISCOUNT,
];

export function GroupPolicies() {
  const { policies } = useCurrentGroup();
  const [selectedFilter, setSelectedFilter] = useState<PolicyType | null>(null);
  const quoteTerm = useAccountingSystemTerm(`Quotes`);
  const quoteTermSingular = useAccountingSystemTerm(`Quote`);
  const featureFlags = useFeatureFlags();
  const allowedPaymentPlans = useAllowedFeature(`paymentPlans`);

  const filterOptions = useMemo(() => {
    const options = Object.keys(langauge.policy.labels)
      .map(key => {
        const policyType = key as PolicyType;

        return { value: policyType, label: langauge.policy.labels[policyType].short.plural };
      })
      .filter(o => {
        if (o.value === PolicyType.PAYMENT_PLAN_REMINDER && (!featureFlags?.paymentPlans || !allowedPaymentPlans)) {
          // Remove payment plans if feature not allowed
          return false;
        }
        // Remove duplicate / grouped types
        const removeTypes = [
          PolicyType.STATEMENT_LATE_FEE,
          PolicyType.CONTACT_ESCALATION,
          PolicyType.ON_DEMAND_PLACEHOLDER,
        ];

        return !removeTypes.includes(o.value);
      })
      .sort((a, b) => {
        return order.indexOf(a.value) - order.indexOf(b.value);
      });

    return options;
  }, [featureFlags, allowedPaymentPlans]);

  const policyTypeCount = useMemo(() => {
    if (!policies || !policies.length) {
      return 0;
    }

    // Count unique policy type groups
    const hasEmails = policies.some(p => p.policy_type === PolicyType.REMINDER);
    const hasLateFees = policies.some(p =>
      [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE].includes(p.policy_type),
    );
    const hasStatements = policies.some(p => p.policy_type === PolicyType.STATEMENT);
    const hasEscalations = policies.some(p =>
      [PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION].includes(p.policy_type),
    );
    const hasSMS = policies.some(p => p.policy_type === PolicyType.SMS);
    const hasDiscounts = policies.some(p => p.policy_type === PolicyType.DISCOUNT);
    const hasQuotes = policies.some(p => p.policy_type === PolicyType.QUOTE_EMAIL);

    return [hasEmails, hasLateFees, hasStatements, hasEscalations, hasSMS, hasDiscounts, hasQuotes]
      .filter(Boolean).length;
  }, [policies]);

  const hasMultipleLateFeeTypes = useMemo(() => {
    if (!policies || !policies.length) {
      return false;
    }

    const hasActiveInvoiceLateFee = policies.some(p =>
      p.policy_type === PolicyType.LATE_FEE && p.state !== PolicyState.INACTIVE,
    );
    const hasActiveStatementLateFee = policies.some(p =>
      p.policy_type === PolicyType.STATEMENT_LATE_FEE && p.state !== PolicyState.INACTIVE,
    );

    return hasActiveInvoiceLateFee && hasActiveStatementLateFee;
  }, [policies]);

  const hasMultipleDiscountPolicies = useMemo(() => {
    if (!policies || !policies.length) {
      return false;
    }

    return policies.filter(p =>
      p.policy_type === PolicyType.DISCOUNT && p.state !== PolicyState.INACTIVE,
    ).length > 1;
  }, [policies]);

  const countsByType = useMemo(() => {
    const counts: Partial<Record<PolicyType, number>> = {};
    if (!policies || !policies.length) {
      return counts as Record<PolicyType, number>;
    }

    const countFor = (types: PolicyType[]) => policies.filter(p => types.includes(p.policy_type)).length;

    counts[PolicyType.REMINDER] = countFor([PolicyType.REMINDER]);
    counts[PolicyType.LATE_FEE] = countFor([PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE]);
    counts[PolicyType.STATEMENT] = countFor([PolicyType.STATEMENT]);
    counts[PolicyType.ESCALATION] = countFor([PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION]);
    counts[PolicyType.SMS] = countFor([PolicyType.SMS]);
    counts[PolicyType.DISCOUNT] = countFor([PolicyType.DISCOUNT]);
    counts[PolicyType.QUOTE_EMAIL] = countFor([PolicyType.QUOTE_EMAIL]);
    counts[PolicyType.PAYMENT_PLAN_REMINDER] = countFor([PolicyType.PAYMENT_PLAN_REMINDER]);

    return counts as Record<PolicyType, number>;
  }, [policies]);

  function onFilterChange(value: PolicyType | null) {
    if (selectedFilter === value) {
      setSelectedFilter(null);
    }
    else {
      setSelectedFilter(value);
    }
  }

  return (
    <div>
      <When condition={ policyTypeCount === 0 }>
        <div className={ `mb-4` }>
          <Notification
            type={ `warning` }
          >
            <Paragraph className={ `font-medium` }>
              { `This group has no policies yet. Any contacts in this group will not have any actions taken on them` }
            </Paragraph>
            <Paragraph
              variant={ `help` }
              className={ `mt-1` }
            >
              { `Policies are automation rules that specify when and what Paidnice should do for you. ` }
            </Paragraph>
          </Notification>
        </div>
      </When>

      <div>
        { /* Filter pills and action buttons in same row */ }
        <div className={ `flex items-center justify-between mb-4 flex-wrap gap-2` }>
          { /* Filter pills */ }
          <div className={ `flex flex-wrap gap-2` }>
            { filterOptions.map((option, idx) => (
              <button
                key={ option.value + idx }
                onClick={ () => onFilterChange(option.value) }
                className={ classNames(
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors`,
                  selectedFilter === option.value
                    ? `bg-paidniceGreen-600 text-white`
                    : `bg-gray-100 text-gray-700 hover:bg-gray-200`,
                ) }
              >
                <span>
                  { option.label }
                  { (countsByType[option.value] || 0) > 0 && (
                    <span className={
                      classNames(
                        `ml-1 text-xs`,
                        selectedFilter === option.value ? `text-white` : `text-gray-600`,
                      )
                    }>
                      { `(${countsByType[option.value]})` }
                    </span>
                  ) }
                </span>
              </button>
            )) }
          </div>

          { /* Action buttons */ }
          <SetUpForMe />
        </div>

        { /* Single wide column with grouped policies by type */ }
        <div className={ `flex flex-col gap-6` }>
          <When condition={ selectedFilter === null|| selectedFilter === PolicyType.REMINDER }>
            <PolicyColumn
              policyTypes={ [PolicyType.REMINDER] }
              title={ `Emails` }
              description={ `Automated email reminders and follow-ups for invoices` }
              subsections={ [
                {
                  title: `Reminders`,
                  filter: policy => [PolicyTargetStatus.DUE, PolicyTargetStatus.OLDEST_DUE, PolicyTargetStatus.INVOICE_AMOUNT_UPDATED, PolicyTargetStatus.PROMISE_BROKEN, PolicyTargetStatus.PROMISE_DUE].includes(policy.target_status),
                  addNewOptions: [
                    { policyType: PolicyType.REMINDER, label: `Add Invoice Reminder` },
                  ],
                },
                {
                  title: `Notifications`,
                  filter: policy => [PolicyTargetStatus.ISSUED, PolicyTargetStatus.MARKED_PAID].includes(policy.target_status),
                  addNewOptions: [
                    {
                      policyType: PolicyType.REMINDER,
                      label: `Add Paid Thank You Email`,
                      defaultOptions: {
                        target_status: PolicyTargetStatus.MARKED_PAID,
                        day_index: 1,
                      },
                    },
                    {
                      policyType: PolicyType.REMINDER,
                      label: `Add Issued Email`,
                      defaultOptions: {
                        target_status: PolicyTargetStatus.ISSUED,
                        day_index: 0,
                      },
                    },
                  ],
                },
              ] }
            />
          </When>
          <When condition={ selectedFilter === null || selectedFilter === PolicyType.LATE_FEE }>
            <PolicyColumn
              policyTypes={ [PolicyType.LATE_FEE, PolicyType.STATEMENT_LATE_FEE] }
              title={ `Late Fees & Interest` }
              description={ `Automated late fees and interest charges for overdue invoices` }
              addNewOptions={ [
                { policyType: PolicyType.LATE_FEE, label: `Add Invoice Late Fee` },
                { policyType: PolicyType.STATEMENT_LATE_FEE, label: `Add Statement Interest Charge` },
              ] }
              duplicateWarning={ hasMultipleLateFeeTypes ? {
                title: `This group has both invoice late fee and statement interest charge policies. This may result in duplicate fees being applied!`,
                body: `We recommend deleting one late fee type to ensure you have only "Invoice" or "Contact" late fees.`,
              } : undefined }
            />
          </When>
          <When condition={ selectedFilter === null|| selectedFilter === PolicyType.STATEMENT }>
            <PolicyColumn
              policyTypes={ [PolicyType.STATEMENT] }
              title={ `Statements` }
              description={ `Scheduled recurring statement sends to contacts` }
              addNewOptions={ [
                { policyType: PolicyType.STATEMENT, label: `Add Statement Policy` },
              ] }
            />
          </When>
          <When condition={ selectedFilter === null|| selectedFilter === PolicyType.ESCALATION }>
            <PolicyColumn
              policyTypes={ [PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION] }
              title={ `Escalations` }
              description={ `Manual tasks created when invoices are unpaid` }
              addNewOptions={ [
                { policyType: PolicyType.ESCALATION, label: `Add Escalation` },
              ] }
            />
          </When>
          <When condition={ allowedPaymentPlans && featureFlags?.paymentPlans }>
            <When condition={ selectedFilter === null|| selectedFilter === PolicyType.PAYMENT_PLAN_REMINDER }>
              <PolicyColumn
                policyTypes={ [PolicyType.PAYMENT_PLAN_REMINDER] }
                title={ `Payment Plan Reminders` }
                description={ `Send email reminders about upcoming and overdue payment plan installments` }
                addNewOptions={ [
                  { policyType: PolicyType.PAYMENT_PLAN_REMINDER, label: `Add Payment Plan Reminder` },
                ] }
              />
            </When>
          </When>
          <When condition={ selectedFilter === null|| selectedFilter === PolicyType.SMS }>
            <PolicyColumn
              policyTypes={ [PolicyType.SMS] }
              title={ `SMS` }
              description={ `Automated SMS alerts for invoices` }
              addNewOptions={ [
                { policyType: PolicyType.SMS, label: `Add SMS Alert` },
              ] }
            />
          </When>
          <When condition={ selectedFilter === null|| selectedFilter === PolicyType.DISCOUNT }>
            <PolicyColumn
              policyTypes={ [PolicyType.DISCOUNT] }
              title={ `Discounts` }
              description={ `Automatic prompt payment discounts applied to invoices` }
              addNewOptions={ [
                { policyType: PolicyType.DISCOUNT, label: `Add Discount Policy` },
              ] }
              duplicateWarning={ hasMultipleDiscountPolicies ? {
                title: `This group has more than one active discount policy. An invoice only receives one discount, so when more than one applies the oldest policy is used.`,
                body: `Make sure the policies target different invoices, or delete one to avoid unexpected results.`,
              } : undefined }
            />
          </When>
          <When condition={ selectedFilter === null|| selectedFilter === PolicyType.QUOTE_EMAIL }>
            <PolicyColumn
              policyTypes={ [PolicyType.QUOTE_EMAIL] }
              title={ quoteTerm }
              description={ `Automated email reminders and follow-ups for ${quoteTerm.toLowerCase()}` }
              addNewOptions={ [
                { policyType: PolicyType.QUOTE_EMAIL, label: `Add ${quoteTermSingular} Reminder` },
              ] }
            />
          </When>
        </div>
      </div>
    </div>
  );
}
